import { CollateralAllocationDetail, DealDetail, RepoType, ValidationIssue } from 'api'
import { createInvalidValueValidationIssue } from 'model'
import { Locale, Translation } from 'locale'
import { isNotNil } from 'utils/predicate'
import { v4 } from 'uuid'
import { SpreadsheetParseValidationResult } from '../allocationDialogs/fundAssignmentDialog/upload/validateTradingEntityAssignmentParseResult'
import {
  AllocationData,
  CollateralAllocationParseResult,
  CollateralBasketAllocationFieldNames
} from './collateralBasketAllocationUploadParse'

interface CollateralAllocationParseResultValidatorContext {
  allocationDetails: CollateralAllocationDetail[]
  dealDetail: DealDetail
}

export interface CollateralAllocationValidationResult extends SpreadsheetParseValidationResult<AllocationData> {}

export const validateCollateralAllocationParseResult = (context: CollateralAllocationParseResultValidatorContext) => ({
  pendingAllocations
}: CollateralAllocationParseResult): CollateralAllocationValidationResult[] => {
  const {
    allocationDetails,
    dealDetail: {
      deal: { repoType }
    }
  } = context

  const allocatableIsins = new Set(allocationDetails.flatMap(({ collateral }) => [collateral.id, collateral.isin]))
  const hasMixedAllocations =
    pendingAllocations.some(a => isNotNil(a.notional)) && pendingAllocations.some(a => isNotNil(a.startConsideration))

  return pendingAllocations.map<CollateralAllocationValidationResult>(allocation => {
    let { isin, haircut, allInPrice } = allocation

    const validationIssues: ValidationIssue[] = []

    if (hasMixedAllocations) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          CollateralBasketAllocationFieldNames.notional,
          Locale.translate(Translation.SC_ERROR_CLIPBOARD_MIXED_TRADING_TYPE)
        )
      )
      validationIssues.push(
        createInvalidValueValidationIssue(
          CollateralBasketAllocationFieldNames.startConsideration,
          Locale.translate(Translation.SC_ERROR_CLIPBOARD_MIXED_TRADING_TYPE)
        )
      )
    }

    if (!allocatableIsins.has(isin)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          CollateralBasketAllocationFieldNames.isin,
          Locale.translate(Translation.SC_ERROR_CLIPBOARD_INVALID_ISIN)
        )
      )
    }

    if (isNotNil(haircut) && repoType === RepoType.STOCK_BORROWING_LENDING) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          CollateralBasketAllocationFieldNames.haircut,
          Locale.translate(Translation.SC_ALLOCATION_UPLOAD_ERROR_NO_HAIRCUT_SBL)
        )
      )
    }

    if (repoType !== RepoType.CLASSIC && isNotNil(allInPrice)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          CollateralBasketAllocationFieldNames.allInPrice,
          Locale.translate(Translation.SC_ALLOCATION_UPLOAD_ERROR_ALL_IN_PRICE_CLASSIC_ONLY)
        )
      )
    }

    return {
      item: {
        _id: v4(),
        ...allocation,
        haircut
      },
      validationIssues
    }
  })
}
